import React, { useState, useEffect, useCallback, useRef } from 'react';

const TOOLTIP_GAP = 12;

function getTargetRect(selector) {
    if (!selector) return null;
    const el = document.querySelector(selector);
    if (!el) return null;
    return el.getBoundingClientRect();
}

function Walkthrough({ isOpen, steps = [], currentStep: controlledStep, onNext, onComplete, onSkip, onClose, navigate }) {
    const [internalStep, setInternalStep] = useState(0);
    const tooltipRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0, placement: 'below' });

    const step = controlledStep != null ? controlledStep : internalStep;
    const current = steps[step];
    const isLast = step >= steps.length - 1;

    const reposition = useCallback(() => {
        if (!current) return;
        const rect = getTargetRect(current.target);
        const tt = tooltipRef.current;
        if (!tt) return;
        const ttRect = tt.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        if (!rect) {
            setPos({ top: vh / 2 - ttRect.height / 2, left: Math.max(12, (vw - ttRect.width) / 2), placement: 'center' });
            return;
        }

        let placement = current.tooltipPlacement || 'below';
        let top, left;

        if (placement === 'below' && rect.bottom + TOOLTIP_GAP + ttRect.height > vh) placement = 'above';
        if (placement === 'above' && rect.top - TOOLTIP_GAP - ttRect.height < 0) placement = 'below';

        if (placement === 'below') {
            top = rect.bottom + TOOLTIP_GAP;
        } else {
            top = rect.top - TOOLTIP_GAP - ttRect.height;
        }

        left = rect.left + rect.width / 2 - ttRect.width / 2;
        left = Math.max(12, Math.min(left, vw - ttRect.width - 12));
        top = Math.max(12, Math.min(top, vh - ttRect.height - 12));

        setPos({ top, left, placement });
    }, [current]);

    useEffect(() => {
        if (!isOpen || !current) return;
        const id = requestAnimationFrame(reposition);
        const onScroll = () => requestAnimationFrame(reposition);
        window.addEventListener('resize', onScroll);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            cancelAnimationFrame(id);
            window.removeEventListener('resize', onScroll);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [isOpen, current, step, reposition]);

    useEffect(() => {
        if (isOpen && current?.target) {
            const el = document.querySelector(current.target);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [isOpen, step, current]);

    useEffect(() => { if (isOpen) setInternalStep(0); }, [isOpen]);

    const advance = useCallback(() => {
        if (current?.navigateTo && navigate) {
            if (current.walkthroughNextState) {
                localStorage.setItem('walkthroughState', JSON.stringify({ active: true, ...current.walkthroughNextState }));
            }
            navigate(current.navigateTo, { state: current.navigationState || {} });
            onComplete?.();
            return;
        }
        if (isLast) { onComplete?.(); return; }
        if (controlledStep == null) {
            setInternalStep(s => s + 1);
        } else {
            onNext?.();
        }
    }, [current, isLast, controlledStep, navigate, onComplete, onNext]);

    const goBack = useCallback(() => {
        if (step > 0 && controlledStep == null) setInternalStep(s => s - 1);
    }, [step, controlledStep]);

    if (!isOpen || !current) return null;

    const rect = getTargetRect(current.target);
    const showBack = step > 0 && !current.waitForAction;
    const showNext = !current.waitForAction;

    return (
        <div className="wt-root">
            {/* Spotlight — box-shadow creates the dark overlay, cutout shows the target element clearly */}
            {rect ? (
                <div
                    className="wt-spotlight"
                    style={{
                        top: rect.top - 6,
                        left: rect.left - 6,
                        width: rect.width + 12,
                        height: rect.height + 12,
                        borderRadius: 12,
                        pointerEvents: 'none',
                    }}
                />
            ) : (
                /* No target — full dark backdrop */
                <div className="wt-backdrop" />
            )}

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className="wt-tooltip"
                style={{ top: pos.top, left: pos.left }}
            >
                {/* Step counter */}
                <div className="wt-counter">{step + 1} / {steps.length}</div>

                <h3 className="wt-title">{current.title}</h3>
                <p className="wt-body">{current.content}</p>

                {current.interactionHint && (
                    <p className="wt-hint">{current.interactionHint}</p>
                )}

                <div className="wt-actions">
                    <button className="wt-btn wt-btn-skip" onClick={() => onSkip?.()}>Skip</button>
                    <div className="wt-actions-right">
                        {showBack && (
                            <button className="wt-btn wt-btn-back" onClick={goBack}>Back</button>
                        )}
                        {showNext && (
                            <button className="wt-btn wt-btn-next" onClick={advance}>
                                {isLast ? 'Done' : 'Next'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .wt-root {
                    position: fixed;
                    inset: 0;
                    z-index: 99999;
                    pointer-events: none;
                }
                .wt-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.55);
                    pointer-events: none;
                    z-index: 1;
                }
                .wt-spotlight {
                    position: fixed;
                    background: transparent;
                    box-shadow: 0 0 0 9999px rgba(0,0,0,0.55);
                    z-index: 1;
                    transition: all 0.3s ease;
                }
                .wt-tooltip {
                    position: fixed;
                    z-index: 2;
                    background: #fff;
                    border-radius: 14px;
                    padding: 18px 20px 14px;
                    width: min(92vw, 360px);
                    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                    pointer-events: auto;
                    transition: top 0.25s ease, left 0.25s ease;
                }
                .wt-counter {
                    font-size: 11px;
                    font-weight: 600;
                    color: #9ca3af;
                    margin-bottom: 4px;
                    letter-spacing: 0.5px;
                }
                .wt-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #111827;
                    margin: 0 0 6px;
                    line-height: 1.3;
                }
                .wt-body {
                    font-size: 13.5px;
                    color: #4b5563;
                    margin: 0 0 10px;
                    line-height: 1.55;
                }
                .wt-hint {
                    font-size: 12px;
                    font-weight: 500;
                    color: #16a34a;
                    margin: 0 0 12px;
                    padding: 6px 10px;
                    background: #f0fdf4;
                    border-radius: 8px;
                }
                .wt-actions {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    padding-top: 4px;
                    border-top: 1px solid #f3f4f6;
                }
                .wt-actions-right {
                    display: flex;
                    gap: 8px;
                }
                .wt-btn {
                    font-size: 13px;
                    font-weight: 600;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 16px;
                    cursor: pointer;
                    transition: background 0.15s;
                    -webkit-tap-highlight-color: transparent;
                }
                .wt-btn-skip {
                    background: transparent;
                    color: #9ca3af;
                }
                .wt-btn-skip:hover { color: #6b7280; }
                .wt-btn-back {
                    background: #f3f4f6;
                    color: #374151;
                }
                .wt-btn-back:hover { background: #e5e7eb; }
                .wt-btn-next {
                    background: #16a34a;
                    color: #fff;
                }
                .wt-btn-next:hover { background: #15803d; }
            `}</style>
        </div>
    );
}

export default Walkthrough;
